import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusPill, ProgressRing } from "@/components/shared/Visual";
import {
  Bell, Menu, Activity, Clock, AlertTriangle, Users,
  ChevronRight, MessageSquare, Phone, ClipboardList,
} from "lucide-react";

/**
 * OPERATIONS MANAGER — mobile-first
 * Compact card feed of live/upcoming deployments, alerts, and quick actions.
 * Accessible to company (as an operational lens on their own bookings).
 */
export default function OpsManager() {
  const { user } = useAuth();
  const [data, setData] = useState({ deployments: [], summary: null });
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    const load = () => {
      api.get("/ops/deployments").then((r) => setData(r.data || {})).catch(() => {});
      api.get("/ops/alerts").then((r) => setAlerts(r.data?.alerts || [])).catch(() => {});
    };
    load();
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  }, []);

  const s = data.summary || { live: 0, starting_soon: 0, upcoming: 0 };
  const active = (data.deployments || []).filter((d) => d.status === "live" || d.status === "starting_soon" || d.status === "upcoming");
  const greeting = timeGreeting();

  return (
    <div className="space-y-5 max-w-md mx-auto lg:max-w-2xl" data-testid="ops-manager-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="txt-md font-manrope text-sm">{greeting}, {user?.contact_name?.split(" ")[0] || "Ops"}</div>
          <div className="eyebrow eyebrow-brand mt-0.5">Operations overview</div>
        </div>
        <div className="relative">
          <button className="w-10 h-10 rounded-full card-inner flex items-center justify-center relative" aria-label="Alerts">
            <Bell className="w-4 h-4 txt-md" />
            {alerts.length > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-[10px] font-bold text-white flex items-center justify-center">
                {alerts.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Top stat row */}
      <div className="grid grid-cols-3 gap-3">
        <MobileStat label="Active" value={s.live} icon={Activity} tone="emerald" testId="om-stat-live" />
        <MobileStat label="Starting Soon" value={s.starting_soon} icon={Clock} tone="amber" testId="om-stat-starting" />
        <MobileStat label="Alerts" value={alerts.length} icon={AlertTriangle} tone={alerts.length ? "rose" : "brand"} testId="om-stat-alerts" />
      </div>

      {/* Deployments list */}
      <div className="space-y-3">
        <div className="eyebrow eyebrow-brand flex items-center justify-between">
          <span>My Deployments</span>
          <span className="txt-lo">{active.length}</span>
        </div>
        {active.length === 0 ? (
          <div className="card-dark p-8 text-center">
            <ClipboardList className="w-8 h-8 txt-lo mx-auto mb-2" />
            <div className="txt-md font-manrope text-sm">No active deployments</div>
          </div>
        ) : active.map((d) => (
          <DeploymentCard key={d.id} d={d} />
        ))}
      </div>

      {/* Alerts feed */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="om-alerts-list">
          <div className="eyebrow eyebrow-brand">Alerts</div>
          {alerts.map((a) => (
            <Link to={`/ops-room/${a.booking_id}`} key={a.id} className={`alert-card ${a.severity === "critical" ? "" : "warn"} block hover:bg-white/5`}>
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${a.severity === "critical" ? "text-rose-400" : "text-amber-400"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-outfit font-semibold txt-hi text-sm">{a.title}</div>
                <div className="eyebrow mt-1 truncate">{a.sub}</div>
              </div>
              <ChevronRight className="w-4 h-4 txt-lo" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function timeGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function MobileStat({ label, value, icon: Icon, tone, testId }) {
  const bg = { emerald: "bg-gold-500/15 text-gold-400", amber: "bg-amber-500/15 text-amber-400", rose: "bg-rose-500/15 text-rose-400", brand: "bg-brand/15 text-brand" }[tone] || "bg-brand/15 text-brand";
  return (
    <div className="card-dark p-3" data-testid={testId}>
      <div className={`w-8 h-8 rounded-md ${bg} flex items-center justify-center mb-2`}>
        <Icon className="w-4 h-4" strokeWidth={2} />
      </div>
      <div className="font-outfit font-bold text-2xl txt-hi leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-widest txt-lo font-outfit mt-1">{label}</div>
    </div>
  );
}

function DeploymentCard({ d }) {
  const kind = d.status === "live" ? "live" : d.status === "starting_soon" ? "starting" : "upcoming";
  const kindLabel = d.status === "live" ? "Live" : d.status === "starting_soon" ? "Starting soon" : "Upcoming";
  return (
    <Link to={d.ops_room ? `/ops-room/${d.id}` : `/company/bookings/${d.id}`} className={`deploy-card ${kind} block hover:border-brand/40`} data-testid={`om-deploy-${d.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            {d.status === "live" && <StatusPill kind="live">Live</StatusPill>}
            {d.status === "starting_soon" && <StatusPill kind="standby">Soon</StatusPill>}
            {d.status === "upcoming" && <StatusPill kind="tracked">Upcoming</StatusPill>}
          </div>
          <div className="font-outfit font-bold txt-hi text-base">{d.title}</div>
          <div className="text-xs txt-lo font-manrope mt-1 flex flex-wrap gap-x-2">
            {d.start_time && <span>{d.start_time} – {d.end_time}</span>}
            <span>·</span>
            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {d.workers}</span>
          </div>
        </div>
        <ProgressRing value={d.progress_pct} size={64} stroke={6} label={`${d.checked_in}/${d.workers}`} sub={kindLabel} />
      </div>
      {d.late > 0 && (
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-amber-400 font-outfit font-bold uppercase tracking-widest">{d.late} Late</span>
          <div className="flex gap-1">
            <button className="w-7 h-7 rounded-md bg-gold-500/15 text-gold-400 flex items-center justify-center" aria-label="Call"><Phone className="w-3.5 h-3.5" /></button>
            <button className="w-7 h-7 rounded-md bg-brand/15 text-brand flex items-center justify-center" aria-label="Message"><MessageSquare className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}
    </Link>
  );
}
